import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { getNotes, postNote, putNote, deleteNote } from "../../../../api/notesApi";
import { ConfirmationPopup } from "../../../../components/confirmationPopup/ConfirmationPopup";
import { addNoteReloadListener, removeNoteReloadListener, triggerNoteReload } from "../../../../utils/dataSyncTriggers";
import { getNotesCache, setNotesCache, updateNoteInCache, addNoteToCache, removeNoteFromCache } from "../../../../utils/notesCache";
import './NotesWidget.css';

interface INote {
    id?: string;
    title: string;
    text: string;
    date: Date;
    createdAt?: string;
    updatedAt?: string;
    client?: string;
}

export const NotesWidget = () => {
    const { translate } = useContext(AppContext);
    const { selectedClient, setActiveTag } = useContext(DashboardContext);
    const [notes, setNotes] = useState<INote[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
    const [editingNote, setEditingNote] = useState<INote | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [boundToClient, setBoundToClient] = useState(false);
    const [filterByClient, setFilterByClient] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const skipNextReloadRef = useRef(false);
    const editingNoteRef = useRef<INote | null>(null);
    const boundToClientRef = useRef(false);
    const flushPendingSaveRef = useRef<(() => void) | null>(null);

    const loadNotes = useCallback(async () => {
        if (skipNextReloadRef.current) {
            skipNextReloadRef.current = false;
            return;
        }
        
        try {
            const cached = getNotesCache();
            if (cached && cached.length > 0) {
                setNotes(cached as INote[]);
                setLoading(false);
            } else {
                setLoading(true);
            }

            const response = await getNotes();
            if (response && response.ok) {
                const data = await response.json();
                const parsedNotes = data.map((item: any) => {
                    const noteId = Object.keys(item)[0];
                    return {
                        id: noteId,
                        title: item[noteId].title || 'Untitled',
                        text: item[noteId].text || '',
                        date: item[noteId].date ? new Date(item[noteId].date) : new Date(),
                        createdAt: item[noteId].createdAt,
                        updatedAt: item[noteId].updatedAt,
                        client: item[noteId].client
                    };
                });
                
                const sortedNotes = parsedNotes.sort((a: INote, b: INote) => {
                    const dateA = new Date(a.updatedAt || a.createdAt || a.date || 0).getTime();
                    const dateB = new Date(b.updatedAt || b.createdAt || b.date || 0).getTime();
                    return dateB - dateA;
                });
                
                setNotesCache(sortedNotes);
                setNotes(sortedNotes);
            }
        } catch (err) {
            console.error('Failed to load notes:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        if (selectedClient?.id) {
            loadNotes();
        }
    }, [selectedClient?.id, loadNotes]);

    useEffect(() => {
        addNoteReloadListener(loadNotes);
        
        return () => {
            removeNoteReloadListener(loadNotes);
        };
    }, [loadNotes]);

    const handleCreateNote = async () => {
        const shouldBindToClient = (filterByClient || boundToClient) && selectedClient?.id;
        const newNote: INote = {
            title: translate('notes_new_title') || 'New Note',
            text: '',
            date: new Date(),
            client: shouldBindToClient ? selectedClient.id : undefined
        };

        try {
            const postResponse = await postNote({
                title: newNote.title,
                text: newNote.text,
                date: newNote.date,
                tags: [],
                client: newNote.client
            });

            if (!postResponse || !postResponse.ok) return;

            // Server doesn't return ID in POST response — reload and find the new note
            const listResponse = await getNotes();
            if (!listResponse || !listResponse.ok) return;
            const listData = await listResponse.json();

            const parsedAll: INote[] = listData.map((item: any) => {
                const id = Object.keys(item)[0];
                return {
                    id,
                    title: item[id].title || '',
                    text: item[id].text || '',
                    date: item[id].date ? new Date(item[id].date) : new Date(),
                    createdAt: item[id].createdAt,
                    updatedAt: item[id].updatedAt,
                    client: item[id].client
                };
            });

            const matchingNotes = parsedAll.filter(n => n.title === newNote.title && n.client === newNote.client);
            const savedNote = matchingNotes.sort((a, b) =>
                new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime()
            )[0];

            if (!savedNote || !savedNote.id) return;

            const sortedNotes = parsedAll.sort((a, b) =>
                new Date(b.updatedAt || b.createdAt || b.date).getTime() - new Date(a.updatedAt || a.createdAt || a.date).getTime()
            );
            setNotes(sortedNotes);
            setNotesCache(sortedNotes);
            setExpandedNoteId(savedNote.id);
            setEditingNote(savedNote);
            skipNextReloadRef.current = true;
            triggerNoteReload();
        } catch (err) {
            console.error('Failed to create note:', err);
        }
    };

    useEffect(() => {
        editingNoteRef.current = editingNote;
    }, [editingNote]);

    useEffect(() => {
        boundToClientRef.current = boundToClient;
    }, [boundToClient]);

    const autoSaveNote = async (noteToSave: INote, clientIdOverride?: string) => {
        if (!noteToSave || !noteToSave.id) return;
        
        const clientId = clientIdOverride !== undefined ? clientIdOverride : (boundToClientRef.current && selectedClient?.id ? selectedClient.id : undefined);
        
        setIsSaving(true);
        try {
            await putNote({
                title: noteToSave.title,
                text: noteToSave.text,
                date: noteToSave.date,
                tags: [],
                client: clientId
            }, noteToSave.id);
            
            setNotes(prev => prev.map(n => n.id === noteToSave.id ? noteToSave : n));
            updateNoteInCache(noteToSave);
        } catch (err) {
            console.error('Failed to save note:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const flushPendingSave = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }
        if (editingNoteRef.current && editingNoteRef.current.id) {
            const clientId = boundToClientRef.current && selectedClient?.id ? selectedClient.id : undefined;
            autoSaveNote(editingNoteRef.current, clientId);
        }
    };

    useEffect(() => {
        flushPendingSaveRef.current = flushPendingSave;
    });

    useEffect(() => {
        return () => {
            flushPendingSaveRef.current?.();
        };
    }, []);

    useEffect(() => {
        if (!editingNote || !editingNote.id) return;
        
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        saveTimeoutRef.current = setTimeout(() => {
            autoSaveNote(editingNote);
        }, 3000);
        
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [editingNote?.title, editingNote?.text]);

    const handleDeleteNote = async (noteId: string) => {
        try {
            await deleteNote(noteId);
            setNotes(prev => prev.filter(n => n.id !== noteId));
            removeNoteFromCache(noteId);
            setExpandedNoteId(null);
            setDeleteConfirmId(null);
            skipNextReloadRef.current = true;
            triggerNoteReload();
        } catch (err) {
            console.error('Failed to delete note:', err);
        }
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushPendingSave();
            }
        };
        const handleBeforeUnload = () => {
            flushPendingSave();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const toggleNoteExpanded = (noteId: string) => {
        if (expandedNoteId === noteId) {
            flushPendingSave();
            setExpandedNoteId(null);
            setEditingNote(null);
        } else {
            if (editingNote) {
                flushPendingSave();
            }
            setExpandedNoteId(noteId);
            const note = notes.find(n => n.id === noteId);
            if (note) {
                setBoundToClient(!!note.client);
            }
        }
    };

    useEffect(() => {
        return () => {
            flushPendingSave();
        };
    }, []);

    // Sync editor content when editing note changes
    useEffect(() => {
        if (editingNote && editorRef.current) {
            editorRef.current.innerHTML = editingNote.text || '';
        }
    }, [editingNote?.id]);

    const handleEditorInput = () => {
        if (!editorRef.current || !editingNote) return;
        
        // Update the editing note with current editor content
        const currentHTML = editorRef.current.innerHTML;
        setEditingNote(prev => prev ? { ...prev, text: currentHTML } : null);
    };

    const formatDate = (dateString: string | Date | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, { 
            month: 'short', 
            day: 'numeric'
        });
    };

    const stripHtml = (html: string) => {
        return html.replace(/<[^>]*>/g, '');
    };

    const formatText = (command: string, value?: string) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
    };

    // Filter notes based on filterByClient state
    const filteredNotes = filterByClient && selectedClient?.id
        ? notes.filter(note => note.client === selectedClient.id)
        : notes;

    return (
        <div className="dashboard_main_block dashboard_main_notes_widget">
            <div className="dashboard_main_block_head">
                <div className="notes_widget_title_wrapper">
                    <div className="dashboard_main_block_title">
                        {translate('notes_widget_title') || 'Notes'}
                    </div>
                    {selectedClient && (
                        <div 
                            className={`notes_widget_filter_icon ${!filterByClient ? 'notes_widget_filter_icon--active' : ''}`}
                            onClick={() => setFilterByClient(!filterByClient)}
                            title={filterByClient 
                                ? translate('filter_show_client_notes').replace('{name}', selectedClient.name)
                                : translate('filter_show_all_notes')
                            }
                        />
                    )}
                </div>
                <div className="dashboard_main_block_head_right">
                    <div className="notes_widget_add_wrapper" onClick={handleCreateNote}>
                        <div className="notes_widget_add_button"></div>
                        <span className="notes_widget_add_text">{translate('notes_add_new') || 'Add a new note'}</span>
                    </div>
                </div>
            </div>
            
            <div className="notes_widget_content">
                {loading ? (
                    <div className="notes_widget_loading">{translate('common_loading') || 'Loading...'}</div>
                ) : (
                    <>
                        <div className="notes_widget_list">
                            {filteredNotes.map((note) => {
                                const isExpanded = expandedNoteId === note.id;
                                
                                return (
                                    <div key={note.id} className={`note_widget_item ${isExpanded ? 'note_widget_item--expanded' : ''}`}>
                                        <div className="note_widget_item_head" onClick={() => !isExpanded && toggleNoteExpanded(note.id!)}>
                                            <div className="note_widget_item_left">
                                                <div className="note_widget_item_title">
                                                    {note.title}
                                                </div>
                                                <div className="note_widget_item_date">
                                                    {formatDate(note.updatedAt || note.createdAt || note.date)}
                                                </div>
                                            </div>
                                            <div className="note_widget_item_actions">
                                                {isExpanded && editingNote?.id !== note.id && (
                                                    <div 
                                                        className="dashboard_main_block_edit_button" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingNote(note);
                                                        }}
                                                    />
                                                )}
                                                <div className="dashboard_main_block_open_button" onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleNoteExpanded(note.id!);
                                                }}>
                                                    
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {isExpanded && editingNote?.id !== note.id && (
                                            <div className="note_widget_item_preview">
                                                <div className="note_widget_preview_text" dangerouslySetInnerHTML={{ __html: note.text || '' }} />
                                            </div>
                                        )}
                                        
                                        {isExpanded && editingNote && (
                                            <div className="note_widget_item_content">
                                                <div className="note_widget_edit_section">
                                                    <input
                                                        type="text"
                                                        className="note_widget_title_input"
                                                        value={editingNote.title}
                                                        onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                                                        placeholder={translate('notes_title_placeholder') || 'Note title'}
                                                    />
                                                    
                                                    {selectedClient && (
                                                        <label className="note_widget_bound_toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={boundToClient}
                                                                onChange={(e) => setBoundToClient(e.target.checked)}
                                                            />
                                                            <span>{translate('notes_bound_to_client')}</span>
                                                        </label>
                                                    )}
                                                    
                                                    {/* Simple toolbar for basic formatting */}
                                                    <div className="note_widget_toolbar">
                                                        <button onClick={() => formatText('bold')} title="Bold" className="note_widget_toolbar_btn">
                                                            <strong>B</strong>
                                                        </button>
                                                        <button onClick={() => formatText('italic')} title="Italic" className="note_widget_toolbar_btn">
                                                            <em>I</em>
                                                        </button>
                                                        <button onClick={() => formatText('underline')} title="Underline" className="note_widget_toolbar_btn">
                                                            <u>U</u>
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Rich text editor */}
                                                    <div
                                                        ref={editorRef}
                                                        className="note_widget_text_editor"
                                                        contentEditable
                                                        onInput={handleEditorInput}
                                                        suppressContentEditableWarning
                                                    />
                                                    
                                                    {isSaving && (
                                                        <div className="note_widget_saving_indicator">
                                                            {translate('common_saving') || 'Saving...'}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="note_widget_footer">
                                                    <div 
                                                        className="note_widget_delete_icon"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteConfirmId(note.id!);
                                                        }}
                                                        title="Delete"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {deleteConfirmId && (
                <ConfirmationPopup
                    message={translate('notes_confirm_delete') || 'Are you sure you want to delete this note?'}
                    confirmText={translate('common_delete') || 'Delete'}
                    cancelText={translate('common_cancel') || 'Cancel'}
                    onConfirm={() => handleDeleteNote(deleteConfirmId)}
                    onCancel={() => setDeleteConfirmId(null)}
                    confirmButtonStyle="danger"
                />
            )}
        </div>
    );
};
